import React from "react";
import { observer } from "mobx-react";
import { Box, CircularProgress, Paper, useTheme } from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { DailyTasksStore } from "../../../../stores/dashboards/DailyTasksStore";
import { DailyTasksSortBy } from "../../../../../../models/Dashboard/DailyTasks/DailyTasks";
import { SortType } from "../../../../../../models/SortType";
import MockupWorkLedger from "./MockupWorkLedger";
import FileWorkLedger from "./FileWorkLedger";
import PrWorkLedger from "./PrWorkLedger";
import PoWorkLedger from "./PoWorkLedger";
import ProjectWorkLedger from "./ProjectWorkLedger";
import RawWorkLedger from "./RawWorkLedger";
import PrintWorkLedger from "./PrintWorkLedger";
import PrintInWorkLedger from "./PrintInWorkLedger";
import DailyTasksTable from "./DailyTasksTable";
import { DailyTasksSectionTitle, DAILY_TASKS_HINTS } from "./DailyTasksHint";
import { dailyTasksStageUi } from "./DailyTasksStageUi";

interface DailyTasksWorkQueueProps {
  store: DailyTasksStore;
}

const DailyTasksWorkQueue: React.FC<DailyTasksWorkQueueProps> = observer(
  ({ store }) => {
    const theme = useTheme();
    const stageUi = dailyTasksStageUi(store.filters.stage);

    return (
      <Paper
        sx={{
          mt: 3,
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          boxShadow:
            "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          position: "relative",
        }}
      >
        {store.queueLoading ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.45)",
              zIndex: 1,
              borderRadius: 2,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : null}

        <DailyTasksSectionTitle
          title="Open work"
          hint={DAILY_TASKS_HINTS[stageUi.openWorkHint]}
          subtitle="Each card is one work state. Click the card for all of that state, or Due today / Past SLA on the card. Completing work stays in Commander."
        />

        {store.queueError ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="4rem"
            textAlign="center"
            border="1px dashed"
            borderColor="divider"
            borderRadius={2}
            px={2}
            mb={2}
          >
            <CustomTypography
              variant="heading6"
              fontWeight={500}
              color={theme.palette.error.main}
            >
              {store.queueError}
            </CustomTypography>
          </Box>
        ) : null}

        {store.filters.stage === "projects" ? (
          <ProjectWorkLedger
            summary={store.projectWork}
            workState={store.filters.mockup_work_state}
            workBucket={store.filters.work_bucket}
            onSelect={(tile, urgency) => {
              void store.selectWorkTile(tile, urgency);
            }}
          />
        ) : store.filters.stage === "file_upload" ? (
          <FileWorkLedger
            summary={store.fileWork}
            workState={store.filters.mockup_work_state}
            workBucket={store.filters.work_bucket}
            onSelect={(tile, urgency) => {
              void store.selectWorkTile(tile, urgency);
            }}
          />
        ) : store.filters.stage === "pr_creation" ? (
          <PrWorkLedger
            summary={store.prWork}
            workState={store.filters.mockup_work_state}
            workBucket={store.filters.work_bucket}
            onSelect={(tile, urgency) => {
              void store.selectWorkTile(tile, urgency);
            }}
          />
        ) : store.filters.stage === "po_creation" ? (
          <PoWorkLedger
            summary={store.poWork}
            workState={store.filters.mockup_work_state}
            workBucket={store.filters.work_bucket}
            onSelect={(tile, urgency) => {
              void store.selectWorkTile(tile, urgency);
            }}
          />
        ) : store.filters.stage === "raw_inward" ? (
          <RawWorkLedger
            summary={store.rawWork}
            workState={store.filters.mockup_work_state}
            workBucket={store.filters.work_bucket}
            onSelect={(tile, urgency) => {
              void store.selectWorkTile(tile, urgency);
            }}
          />
        ) : store.filters.stage === "print_outward" ? (
          <PrintWorkLedger
            summary={store.printWork}
            workState={store.filters.mockup_work_state}
            workBucket={store.filters.work_bucket}
            onSelect={(tile, urgency) => {
              void store.selectWorkTile(tile, urgency);
            }}
          />
        ) : store.filters.stage === "print_inward" ? (
          <PrintInWorkLedger
            summary={store.printInWork}
            workState={store.filters.mockup_work_state}
            workBucket={store.filters.work_bucket}
            onSelect={(tile, urgency) => {
              void store.selectWorkTile(tile, urgency);
            }}
          />
        ) : (
          <MockupWorkLedger
            summary={store.mockupWork}
            workState={store.filters.mockup_work_state}
            workBucket={store.filters.work_bucket}
            onSelect={(tile, urgency) => {
              void store.selectWorkTile(tile, urgency);
            }}
          />
        )}

        <DailyTasksTable
          rows={store.queueRows}
          stage={store.filters.stage}
          page_no={store.filters.page_no}
          limit={store.filters.limit}
          total_count={store.queueTotalCount}
          sortBy={store.filters.sort_by}
          sortType={store.filters.sort_type}
          filters={store.filters}
          onSortChange={(column: DailyTasksSortBy, sortType: SortType) => {
            void store.changeQueueSort(column, sortType);
          }}
          onPageChange={(_event, page) => {
            void store.changeQueuePage(page);
          }}
          onRowsPerPageChange={(limit) => {
            void store.changeQueuePage(1, limit);
          }}
          onRowClick={(row) => {
            store.openProductDrawer(row.order_id, row.product_id, row);
          }}
        />
      </Paper>
    );
  },
);

export default DailyTasksWorkQueue;
